'use client';

import type { ReactNode } from 'react';
import {
  Button,
  Card,
  Popover,
  Separator,
  Tooltip,
  TooltipProvider,
} from '@mokoboko/components';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { ChevronDown, LockIcon } from '@/icons';
import { mergeClassNames } from '@/lib/utils';
import { LockedPopoverContent } from './locked-popover-content';
import { RestrictionAlert, type RestrictionAction } from './restriction-alert';

// A detail card's top padding above its Go back row, trimmed to the space the
// card's gap leaves around the rule under that row.
export const GO_BACK_CARD_TOP = 'pt-4 sm:pt-5 md:pt-6';

export const DETAIL_HERO = 'flex flex-col gap-5 md:flex-row md:gap-16';

export const DETAIL_HERO_TEXT = 'flex min-w-0 flex-1 flex-col gap-5 sm:gap-6';

export const DETAIL_HERO_MEDIA = 'order-1 shrink-0 md:order-2';

export const DetailCard = ({
  hasBack,
  className,
  children,
}: {
  hasBack: boolean;
  className?: string;
  children: ReactNode;
}) => (
  <Card
    className="max-sm:rounded-b-none"
    contentClassName={mergeClassNames(
      'flex flex-col gap-5 p-5 sm:gap-6 sm:p-6 md:p-8',
      hasBack && GO_BACK_CARD_TOP,
      className
    )}
  >
    {children}
  </Card>
);

// A phone has no hover for the tooltip, so a tap on the locked button shows
// the reason, as the receipt's locked Reschedule and Cancel do. The button
// lets taps through to the trigger; a disabled one would swallow them. The
// span is no control and the reason is also written under it, so it drops the
// popup state the trigger would put on it.
const PhoneLockedBuyButton = ({
  label,
  reason,
}: {
  label: string;
  reason: string;
}) => (
  <Popover.Root>
    <Popover.Trigger asChild>
      <span
        className="block w-full"
        aria-haspopup={undefined}
        aria-expanded={undefined}
        aria-controls={undefined}
      >
        <Button
          size="lg"
          fullWidth
          disabled
          leadingIcon={<LockIcon />}
          iconClassName="[&_svg]:!w-[18px] [&_svg]:!h-[18px]"
          className="pointer-events-none"
        >
          {label}
        </Button>
      </span>
    </Popover.Trigger>
    <LockedPopoverContent side="top">{reason}</LockedPopoverContent>
  </Popover.Root>
);

// `phone` is the full-width button under the facts; with a reason, the notice
// moves under it from the top of the card.
type BuyButtonProps = {
  label: string;
  reason?: string;
  // The line under the reason in the notice; the tooltip keeps to the reason.
  reasonDetail?: string | null;
  onClick: () => void;
  reasonAction: RestrictionAction;
};

export const DetailBuyButton = ({
  label,
  reason,
  reasonDetail,
  onClick,
  reasonAction,
  phone = false,
}: BuyButtonProps & { phone?: boolean }) => {
  const isMobile = useIsMobile();

  if (phone && reason) {
    return (
      <div className="flex flex-col gap-3 sm:hidden">
        <PhoneLockedBuyButton label={label} reason={reason} />
        <RestrictionAlert
          reason={reason}
          detail={reasonDetail}
          action={reasonAction}
        />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip content={reason} side="top">
        <span
          className={
            phone ? 'block w-full sm:hidden' : 'hidden w-fit shrink-0 sm:block'
          }
        >
          <Button
            onClick={onClick}
            size={isMobile ? 'lg' : 'md'}
            fullWidth={phone}
            disabled={!!reason}
          >
            {label}
          </Button>
        </span>
      </Tooltip>
    </TooltipProvider>
  );
};

// With no Go back the block holds only the notice, which a phone shows under
// its own buy button instead.
export const DetailTopBar = ({
  hasBack,
  onBack,
  buyButton,
}: {
  hasBack: boolean;
  onBack: () => void;
  buyButton: BuyButtonProps;
}) => {
  const isMobile = useIsMobile();

  return (
    <>
      <div
        className={mergeClassNames(
          'flex flex-col gap-4 empty:hidden',
          !hasBack && 'max-sm:hidden'
        )}
      >
        {hasBack && (
          <div className="flex items-center justify-between">
            <Button
              variant={'secondary-gray'}
              className="w-fit"
              leadingIcon={<ChevronDown rotate={90} />}
              onClick={onBack}
              size={isMobile ? 'lg' : 'md'}
            >
              Go back
            </Button>
            <DetailBuyButton {...buyButton} />
          </div>
        )}
        {buyButton.reason && (
          <RestrictionAlert
            reason={buyButton.reason}
            detail={buyButton.reasonDetail}
            action={buyButton.reasonAction}
            className="max-sm:hidden"
          />
        )}
      </div>
      {hasBack && <Separator />}
    </>
  );
};
